import { useState, useEffect, useCallback } from 'react';

export type PWAPlatform =
    | 'android'       // Chrome/Edge Android → beforeinstallprompt nativo
    | 'android-manual'// Android mas sem beforeinstallprompt (fallback instrução manual)
    | 'ios-safari'    // Safari iOS → share na barra inferior
    | 'ios-chrome'    // Chrome iOS (CriOS) → share na barra superior
    | 'ios-inapp'     // In-app browser (Instagram, WhatsApp, etc.)
    | 'installed'     // Já está rodando como PWA standalone
    | 'unsupported';  // Desktop ou sem suporte

export interface PWAInstallState {
    platform: PWAPlatform;
    canInstall: boolean;
    isVisible: boolean;
    triggerInstall: () => Promise<void>;
    dismiss: () => void;        // Fechar por enquanto (mostra de novo em alguns dias)
    dismissPermanent: () => void; // Nunca mais mostrar (após instalar)
}

// ── Chaves de armazenamento ──────────────────────────────
const SNOOZED_UNTIL_KEY = 'pwa_snoozed_until_v4';   // timestamp de quando pode reaparecer
const INSTALLED_KEY = 'pwa_installed_v4';         // usuário instalou — não mostrar mais

// Cooldown em ms: quanto tempo esperar após o usuário fechar antes de mostrar de novo
const SNOOZE_DAYS = 3;
const SNOOZE_MS = SNOOZE_DAYS * 24 * 60 * 60 * 1000;

// Tempo máximo aguardando beforeinstallprompt no Android (ms).
// O evento é capturado em index.html antes do React carregar.
// Se não está em window.__pwaPrompt ao montar, provavelmente não vai chegar.
const ANDROID_PROMPT_TIMEOUT_MS = 1500;

function detectPlatform(): Exclude<PWAPlatform, 'android-manual'> | 'android' {
    const ua = navigator.userAgent;

    const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;

    if (isStandalone) return 'installed';

    const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;

    if (isIOS) {
        if (/CriOS/.test(ua)) return 'ios-chrome';

        const isInApp =
            /GSA\//.test(ua) ||
            /Instagram/.test(ua) ||
            /FBAN|FBAV/.test(ua) ||
            /WhatsApp/.test(ua) ||
            /Line\//.test(ua) ||
            /Twitter/.test(ua) ||
            /LinkedInApp/.test(ua);

        if (isInApp) return 'ios-inapp';

        const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
        return isSafari ? 'ios-safari' : 'ios-safari'; // fallback para safari
    }

    if (/Android/.test(ua)) return 'android';

    return 'unsupported';
}

/**
 * Verifica se o prompt está em snooze (usuário pediu para mostrar depois).
 * Retorna true se ainda está aguardando o cooldown.
 */
function isSnoozed(): boolean {
    const until = localStorage.getItem(SNOOZED_UNTIL_KEY);
    if (!until) return false;
    return Date.now() < parseInt(until, 10);
}

export function usePWAInstall(): PWAInstallState {
    const [platform, setPlatform] = useState<PWAPlatform>('unsupported');
    const [isVisible, setIsVisible] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

    useEffect(() => {
        const detected = detectPlatform();

        // Se já instalado ou desktop, sair imediatamente
        if (detected === 'installed' || detected === 'unsupported') {
            setPlatform(detected);
            return;
        }

        // Se o usuário instalou anteriormente, não mostrar mais
        if (localStorage.getItem(INSTALLED_KEY) === 'true') {
            setPlatform('installed');
            return;
        }

        // Se ainda está no período de snooze, não mostrar
        if (isSnoozed()) {
            setPlatform(detected);
            return;
        }

        // ─── Android ───────────────────────────────────────────────────────────
        if (detected === 'android') {
            // Verificar se o evento já foi capturado pelo script inline do index.html
            const existingPrompt = (window as any).__pwaPrompt;

            if (existingPrompt) {
                setPlatform('android');
                setDeferredPrompt(existingPrompt);
                const t = setTimeout(() => setIsVisible(true), 800);
                return () => clearTimeout(t);
            }

            // Aguardar o evento 'beforeinstallprompt' por até ANDROID_PROMPT_TIMEOUT_MS
            // Se não chegar, mostrar instrução manual (fallback)
            let resolved = false;

            const handler = (e: Event) => {
                e.preventDefault();
                resolved = true;
                clearTimeout(fallbackTimer);
                (window as any).__pwaPrompt = e;
                setPlatform('android');
                setDeferredPrompt(e);
                setTimeout(() => setIsVisible(true), 800);
            };

            window.addEventListener('beforeinstallprompt', handler as EventListener);

            // Fallback: exibir instrução manual se o evento não chegar
            const fallbackTimer = setTimeout(() => {
                if (!resolved) {
                    setPlatform('android-manual');
                    setIsVisible(true);
                }
            }, ANDROID_PROMPT_TIMEOUT_MS);

            return () => {
                window.removeEventListener('beforeinstallprompt', handler as EventListener);
                clearTimeout(fallbackTimer);
            };
        }

        // ─── iOS (Safari, Chrome, In-App) ──────────────────────────────────────
        setPlatform(detected);
        const t = setTimeout(() => setIsVisible(true), 2000);
        return () => clearTimeout(t);

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Acionar o prompt nativo do Android
    const triggerInstall = useCallback(async () => {
        if (platform === 'android' && deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setIsVisible(false);
                setDeferredPrompt(null);
                (window as any).__pwaPrompt = null;
                localStorage.setItem(INSTALLED_KEY, 'true');
            }
            // Se recusou, só fecha o banner (snooze será controlado pelo usuário)
            else {
                setIsVisible(false);
            }
        }
    }, [platform, deferredPrompt]);

    // Fechar por enquanto — reaparece após SNOOZE_DAYS dias
    const dismiss = useCallback(() => {
        setIsVisible(false);
        const until = Date.now() + SNOOZE_MS;
        localStorage.setItem(SNOOZED_UNTIL_KEY, until.toString());
    }, []);

    // Nunca mais mostrar — apenas após instalação confirmada
    const dismissPermanent = useCallback(() => {
        setIsVisible(false);
        localStorage.setItem(INSTALLED_KEY, 'true');
    }, []);

    const canInstall = platform !== 'installed' && platform !== 'unsupported';

    return { platform, canInstall, isVisible, triggerInstall, dismiss, dismissPermanent };
}
